import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ScrollText, Loader2, ExternalLink, Shield } from 'lucide-react';

const TERMS_SECTIONS = [
    {
        title: '1. Term',
        content: 'This agreement begins on the date of acceptance and continues until either party wishes to terminate with a minimum of thirty (30) days\' advance written notice. HAAPE shall conduct an annual review to determine effectiveness of the membership between HWE Member and HAAPE ("MEMBERSHIP").'
    },
    {
        title: '2. Membership Objective',
        content: 'HWE Member agrees to participate in and assist the HWE by networking with other organizations ("Referrals") in their local geography with the common goal of finding meaningful and sustainable employment for the autistic community. At such time as the Referrals are approved by HAAPE, such Referrals shall enter into an agreement with HAAPE in same format to these Terms. HAAPE has the right to modify these Terms at any time so long as the HWE Member and HAAPE agree to the modified terms in writing.'
    },
    {
        title: '3. Primary Responsibilities',
        content: null,
        subsections: [
            {
                subtitle: 'HAAPE agrees to:',
                items: [
                    'Assist HWE Member in establishing a local/national geographic ecosystem',
                    'Assist and approve Referrals',
                    'Connect/introduce other HWE Members to allow for sharing best practices within the HWE',
                    'Vet all Referrals to ensure mission alignment',
                    'Develop and maintain an HWE database referred to as the HAAPE HUB',
                    'Share Referral information with others in the HWE (depending on the terms of each HWE Membership\'s respective agreement)',
                    'Provide guidance for HWE Members and Referrals to identify employers',
                    'Share resources through the database',
                    'Support the local HWE and its referrals on HAAPE\'s social media and website (as appropriate)',
                    'Assist HWE Members in securing grant and sponsorship funding by providing support letters as appropriate',
                    'Allow HWE Members to offer educational seminars on mutually agreed topics when applicable'
                ]
            },
            {
                subtitle: 'HWE Member agrees to:',
                items: [
                    'Assist HAAPE in establishing a local/national geographic ecosystem',
                    'Share organization and contact information by entering into the HAAPE Hub',
                    'Input key performance data in the HAAPE Hub at least annually (e.g. graduation, hiring, and retention numbers)',
                    'Actively network with HWE Members and Referrals and participate in HAAPE\'s Community of Interest as requested',
                    'Promote HAAPE and the HWE Members and Referrals on social media and website (as appropriate)',
                    'Share employment best practices with HAAPE and the HWE Members and Referrals',
                    'Assist HAAPE to secure grant and sponsorship funding by providing support letters as needed',
                    'Provide graduation and employment success stories and testimonials'
                ]
            }
        ]
    },
    {
        title: '4. Confidentiality',
        content: 'HWE Member and HAAPE shall only share contact information and performance data housed within the HAAPE Hub with other approved referrals of the HWE. HAAPE shall only publish performance data that has been entered by referrals on a consolidated basis. HAAPE may publish testimonials and photos that have been entered into the Hub.'
    },
    {
        title: '5. Compliance With Applicable Laws',
        content: 'Both parties shall comply with all laws, ordinances, rules, and regulations of any government or administrative agency, federal, state or local in performing any and all services related to these Terms. If the Partnership requires sharing of client information, both parties agree to oblige by all other state and federal privacy requirements.'
    },
    {
        title: '6. Governing Law',
        content: 'These Terms shall be governed by and construed in accordance with the internal laws of the State of Florida, without giving effect to any choice or conflict of law provision or rule (whether of the State of Florida or any other jurisdiction) that would cause the application of laws of any jurisdiction other than those of the State of Florida.'
    },
    {
        title: '7. Indemnification',
        content: 'Both parties agree to mutually indemnify and hold the other party harmless against all claims, reasonable attorney\'s fees, liabilities, costs, damages, settlements, or expenses that result from the breach of these Terms, and/or from the willful acts or omissions of either party, its agents, or employees in the performance of any of the services or duties pursuant to or related in any way to these Terms. Any and all obligations under this paragraph shall survive the termination of these Terms.'
    },
    {
        title: '8. Reference',
        content: 'References to "partner" or "member" does not indicate a membership or partnership agreement between the parties under applicable law. These references are terms of art for convenience.'
    },
    {
        title: '9. Counterparts',
        content: 'These Terms may be executed in counterparts, each of which shall be deemed one and the same instrument.'
    }
];

const TermsAcceptancePage = () => {
    const { user, setUser, isAuthenticated, login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const redirectPath = searchParams.get('redirect') || '/directory';

    const handleAccept = async () => {
        if (!agreed) return;
        setSubmitting(true);
        try {
            await authAPI.acceptTerms();
            setUser({ ...user, terms_accepted: true });
            toast.success('Terms accepted. Welcome to HAAPE Hub!');
            // Always go to welcome page after accepting terms
            navigate('/welcome', { replace: true });
        } catch (error) {
            console.error('Failed to accept terms:', error);
            toast.error('Failed to save. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="terms-login-prompt">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <ScrollText className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle>Terms & Conditions</CardTitle>
                        <CardDescription>Please log in to review and accept our terms.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={login} className="w-full" data-testid="terms-login-btn">Member Login</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="terms-acceptance-page">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                        <Shield className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="text-3xl font-bold mb-2">Terms & Conditions</h1>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        Please review and accept the Terms & Conditions below to continue using HAAPE Hub.
                    </p>
                </div>

                {/* Terms & Conditions Document */}
                <Card className="mb-8">
                    <CardHeader className="text-center border-b bg-muted/30">
                        <CardTitle className="text-xl">Terms & Conditions</CardTitle>
                        <CardDescription className="text-base">
                            HAAPE's Workforce Ecosystem Member Partnership
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="py-6">
                        {/* Mission Statement */}
                        <div className="bg-primary/5 border-l-4 border-primary p-4 mb-8 rounded-r">
                            <p className="font-semibold text-primary text-sm leading-relaxed">
                                HAAPE'S MISSION: TO CREATE AUTISM WORKFORCE ECOSYSTEMS FOCUSED ON TRAINING AND EDUCATION THAT EMPOWER AUTISTIC INDIVIDUALS TO OBTAIN MEANINGFUL AND SUSTAINABLE CAREERS AND CONNECTING STAKEHOLDERS TO PROVIDE EMPLOYMENT OPPORTUNITIES FOR THESE INDIVIDUALS.
                            </p>
                        </div>

                        {/* Intro */}
                        <p className="text-muted-foreground mb-6 leading-relaxed">
                            This agreement is made and entered into by and between the applying organization ("HWE Member") and Helping Adults with Autism Perform and Excel (HAAPE), a 501(c)(3) non-profit organization with an EIN # of 81-1709197. By entering into this agreement, HWE Member and HAAPE agree as follows:
                        </p>

                        {/* Sections */}
                        <div className="space-y-6" data-testid="mou-sections">
                            {TERMS_SECTIONS.map((section, idx) => (
                                <div key={idx}>
                                    <h3 className="text-base font-semibold mb-2">{section.title}</h3>
                                    {section.content && (
                                        <p className="text-sm text-muted-foreground leading-relaxed">{section.content}</p>
                                    )}
                                    {section.subsections?.map((sub, si) => (
                                        <div key={si} className="mt-3">
                                            <h4 className="text-sm font-semibold mb-2">{sub.subtitle}</h4>
                                            <ul className="list-disc pl-6 space-y-1">
                                                {sub.items.map((item, ii) => (
                                                    <li key={ii} className="text-sm text-muted-foreground">{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 text-right">
                            <a
                                href="/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                                data-testid="view-full-terms-link"
                            >
                                View full Terms page <ExternalLink className="h-3 w-3" />
                            </a>
                        </div>
                    </CardContent>
                </Card>

                {/* Agreement Checkbox + Accept Button */}
                <Card className="border-primary/20 bg-primary/[0.02]">
                    <CardContent className="py-6">
                        <div className="flex items-start gap-3 mb-6">
                            <Checkbox
                                id="agree-terms"
                                checked={agreed}
                                onCheckedChange={(checked) => setAgreed(!!checked)}
                                data-testid="agree-terms-checkbox"
                                className="mt-0.5"
                            />
                            <label htmlFor="agree-terms" className="text-sm leading-relaxed cursor-pointer select-none">
                                I have read, understood, and agree to be bound by the terms and conditions of this
                                Terms & Conditions on behalf of myself and/or my organization. I understand that
                                this agreement governs my membership in HAAPE's Workforce Ecosystem.
                            </label>
                        </div>
                        <Button
                            onClick={handleAccept}
                            disabled={!agreed || submitting}
                            className="w-full"
                            size="lg"
                            data-testid="accept-terms-btn"
                        >
                            {submitting ? (
                                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Accepting...</>
                            ) : (
                                'Accept Terms & Continue'
                            )}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default TermsAcceptancePage;
